<template>
    <div
        class="form-group has-effect"
        :class="[
            { 'is-invalid': invalid || localInvalid || onlyNumbersInvalid },
            { 'no-icon': !invalidIcon },
            { disabled: disabled }
        ]"
    >
        <div class="has-effect-input">
            <input
                :type="localType"
                class="form-control form-input"
                :class="[
                    { 'has-content': text || localText || onlyNumbersInvalid },
                    { 'is-invalid': invalid },
                    { 'has-icon': tooltip },
                    customClass
                ]"
                v-model="localText"
                v-mask="maskOptions"
                :id="name"
                :name="name"
                :readonly="readonly"
                :required="required"
                :placeholder="placeholder"
                :min="type === 'number' ? min : null"
                :disabled="disabled"
                :maxlength="maxlength"
                @keydown="onKeyPress"
                @blur="$emit('blur', text)"
                @keyup="val => $emit('keyup', val)"
                @change="val => $emit('change', val)"
            />

            <label v-if="label" class="form-label" :for="name">
                <span v-if="label" v-text="label" />
                <span v-if="required" v-text="'*'" class="text-danger pl-1" />
            </label>

            <a-icon
                v-if="type === 'password'"
                :icon="localType === 'password' ? 'Eye' : 'EyeClosed'"
                class="input-icon cursor-pointer"
                @click.native="showPass"
            />

            <button
                v-if="tooltip"
                type="button"
                class="d-flex input-icon"
                v-tooltip="tooltip"
            >
                <a-icon icon="Info" />
            </button>
        </div>
        <div
            v-if="onlyNumbersInvalid || invalidFeedback || localInvalid"
            class="invalid-feedback text-left"
        >
            {{ onlyNumbersInvalid || invalidFeedback || localInvalid }}
        </div>
    </div>
</template>

<script>
import DOMPurify from 'dompurify'

export default {
    props: {
        required: {
            type: Boolean,
            default: false
        },
        type: {
            type: String,
            default: 'text'
        },
        label: {
            type: String,
            required: false
        },
        name: {
            type: String,
            required: false
        },
        text: {
            type: [String, Number],
            required: false
        },
        invalid: {
            type: Boolean,
            default: false
        },
        invalidFeedback: {
            type: String,
            default: 'Wystąpił błąd'
        },
        invalidIcon: {
            type: Boolean,
            default: true
        },
        readonly: {
            type: Boolean,
            default: false
        },
        format: {
            type: String,
            required: false
        },
        customClass: {
            type: String,
            required: false
        },
        placeholder: {
            type: String,
            required: false
        },
        min: {
            type: Number
        },
        max: {
            type: Number
        },
        tooltip: {
            type: String,
            required: false
        },
        preventDecimals: {
            type: Boolean,
            default: false
        },
        validation: {
            type: Function,
            required: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        maxlength: {
            type: Number
        },
        mask: {
            type: [String, Array],
            default: null
        }
    },

    computed: {
        maskOptions() {
            if (!this.mask) return null
            return { mask: this.mask, greedy: true }
        }
    },

    watch: {
        text: {
            immediate: true,
            handler(val) {
                if (val === this.localText) return
                this.localText = val
            }
        },
        localText: {
            handler(val) {
                const sanitizedVal = DOMPurify.sanitize(val)
                if (typeof this.validation === 'function') {
                    const validatedVal = this.validation(sanitizedVal)
                    this.localText = validatedVal
                    this.$emit('input', validatedVal)
                } else if (this.type === 'number') {
                    const newVal = this.validateMinMax(sanitizedVal)
                    this.localText = newVal
                    this.$emit('input', newVal)
                } else {
                    this.$emit('input', sanitizedVal)
                }
            }
        }
    },

    data() {
        return {
            localText: '',
            passVisible: false,
            localInvalid: '',
            onlyNumbersInvalid: '',
            localType: this.type
        }
    },
    methods: {
        showPass() {
            this.localType = this.localType === 'password' ? 'text' : 'password'
        },
        validateMinMax(val) {
            if (+val < +this.min) {
                this.localInvalid = `Pole może zawierać jedynie wartość liczbową nie niższą niż ${this.min.toString()}`
                return this.min.toString()
            } else if (+val > +this.max) {
                this.localInvalid = `Pole może zawierać jedynie wartość liczbową nie przekraczającą ${this.max.toString()}`
                return this.max.toString()
            } else {
                setTimeout(() => {
                    this.localInvalid = ''
                }, 5000)

                return val !== '0' ? val.replace(/^0+/, '') : val
            }
        },
        onKeyPress(e) {
            e = e || window.event
            const charCode = e.which ? e.which : e.keyCode
            if (
                (this.type === 'number' || this.validation === 'localCode') &&
                charCode > 31 &&
                (charCode < 48 || charCode > 57) &&
                !(charCode > 36 && charCode < 45) &&
                !(charCode > 93 && charCode < 106) &&
                !e.metaKey &&
                !e.ctrlKey &&
                !e.shiftKey &&
                charCode !== 46 &&
                charCode !== 188 &&
                charCode !== 189 &&
                charCode !== 108 &&
                charCode !== 110 &&
                charCode !== 190 &&
                charCode !== 229
            ) {
                if (this.validation === 'localCode') {
                    this.onlyNumbersInvalid = 'Dopuszczone są tylko cyfry'
                }
                e.preventDefault()
            } else if (+this.min >= 0 && charCode === 189) {
                e.preventDefault()
            } else if (
                this.preventDecimals &&
                (charCode === 188 ||
                    charCode === 190 ||
                    charCode === 108 ||
                    charCode === 110)
            ) {
                e.preventDefault()
            } else {
                if (this.validation === 'localCode') {
                    this.onlyNumbersInvalid = ''
                }
                return true
            }
        }
    }
}
</script>
